import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type {
  OutreachBulkReserveRequest,
  OutreachBulkSendRequest,
  SaveOutreachSelectionRequest,
} from '@aegislab/shared';
import {
  createCandidateQuery,
  fetchCandidatePage,
  fetchCandidateQuery,
  fetchCandidateStatuses,
  fetchLatestCandidateQuery,
  fetchOtherChannelCandidates,
  fetchOutlierRows,
  fetchOutlierSummary,
  fetchOutreachProgress,
  previewOutreachBulk,
  sendOutreachBulk,
  reserveOutreachBulk,
  cancelOutreachReservation,
  fetchOutreachSelection,
  retryCandidateQuery,
  saveOutreachSelection,
  type CandidateQueryDetail,
  type LatestCandidateQuery,
  type OutreachOutlierGroupCode,
} from '../api/outreachCandidateApi';
import { useCandidateQueryWatchStore } from './candidateQueryWatchStore';

/**
 * 후보 조건 조회 — 퀘스트 `7ca8908c`. IA §3-3.
 *
 * **조회는 비동기이고 한 스냅샷을 끝까지 쓴다.** 원천 3,191만 행에 인덱스가 없어 풀스캔이고
 * 2026-09-22 실측으로 준비까지 124.6~148.7초다. 그래서 접수만 받고 상태를 물어 가며 기다린다.
 *
 * 키에 나라가 들어가는 이유는 초안과 같다 — US 선택이 JP 를 덮으면 안 된다(2026-09-18).
 */
export const candidateKeys = {
  all: ['outreach-candidate'] as const,
  query: (scenarioId: string, queryId: string) =>
    ['outreach-candidate', 'query', scenarioId, queryId] as const,
  latest: (scenarioId: string, country: string) =>
    ['outreach-candidate', 'latest', scenarioId, country] as const,
  page: (scenarioId: string, queryId: string, cursor: string | null) =>
    ['outreach-candidate', 'page', scenarioId, queryId, cursor ?? 'first'] as const,
  selection: (scenarioId: string, country: string) =>
    ['outreach-candidate', 'selection', scenarioId, country] as const,
  otherChannel: (scenarioId: string, queryId: string) =>
    ['outreach-candidate', 'other-channel', scenarioId, queryId] as const,
  statuses: (scenarioId: string, ids: readonly string[]) =>
    ['outreach-candidate', 'statuses', scenarioId, ids] as const,
  progress: (scenarioId: string) => ['outreach-candidate', 'progress', scenarioId] as const,
  bulkPreview: (
    scenarioId: string,
    draftId: string,
    perCreatorSendIds: readonly string[],
    reincludeIds: readonly string[] = []
  ) =>
    [
      'outreach-candidate',
      'bulk-preview',
      scenarioId,
      draftId,
      perCreatorSendIds,
      reincludeIds,
    ] as const,
  outlierSummary: (scenarioId: string, draftId: string) =>
    ['outreach-candidate', 'outlier-summary', scenarioId, draftId] as const,
  outliers: (scenarioId: string, draftId: string, membership: string) =>
    ['outreach-candidate', 'outliers', scenarioId, draftId, membership] as const,
  outlierRows: (scenarioId: string, draftId: string, code: string, offset: number) =>
    ['outreach-candidate', 'outlier-rows', scenarioId, draftId, code, offset] as const,
};

/** 진행 관측 계약의 갱신 주기. */
const POLL_MS = 2_000;

/**
 * 끝난 상태 — 더 물어볼 이유가 없다. `expired` 는 수명 30일이 지난 것이다.
 *
 * **테스트가 직접 잡을 수 있게 내보낸다.** `refetchInterval` 콜백은 타이머 안에서 도는 값이라
 * 훅 테스트가 그 멈춤을 보기 어렵다 — 판정만 순수 함수로 떼어 두면 그건 잡힌다.
 */
export function isCandidateQuerySettled(state: CandidateQueryDetail['state']): boolean {
  return state === 'ready' || state === 'failed' || state === 'expired';
}

const isSettled = isCandidateQuerySettled;

export function useCandidateQuery(scenarioId: string, queryId: string | null) {
  return useQuery({
    queryKey: candidateKeys.query(scenarioId, queryId ?? ''),
    queryFn: () => fetchCandidateQuery(scenarioId, queryId as string),
    enabled: queryId !== null,
    // 끝나면 멈춘다 — 안 멈추면 30일짜리 스냅샷을 5초마다 영원히 묻는다. 상세 조회가
    // 실패해도 멈춘다: 끝난 상태가 영영 안 오므로 사람이 「다시 조회」 로 새로 걸 때까지 기다린다.
    refetchInterval: (query) =>
      query.state.status === 'error' || (query.state.data && isSettled(query.state.data.state))
        ? false
        : POLL_MS,
    // 상태를 보는 화면이라 오래된 값을 보여 주지 않는다.
    staleTime: 0,
  });
}

/**
 * 내가 이 나라에 마지막으로 건 조회. 조회 번호가 화면 state 에만 있으면 준비를 기다리는 2분 사이
 * 나갔다 왔을 때 「조회 중」 이 사라지고 같은 스캔을 다시 걸게 된다(2026-09-29).
 */
export function useLatestCandidateQuery(scenarioId: string, country: string | null) {
  return useQuery({
    queryKey: candidateKeys.latest(scenarioId, country ?? ''),
    queryFn: () => fetchLatestCandidateQuery(scenarioId, country as string),
    enabled: country !== null,
    staleTime: 0,
  });
}

export function useCreateCandidateQuery(scenarioId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { region: string; minFollowers: number; maxFollowers?: number }) =>
      createCandidateQuery(scenarioId, input),
    onSuccess: (accepted, input) => {
      queryClient.setQueryData<LatestCandidateQuery>(
        candidateKeys.latest(scenarioId, input.region),
        { queryId: accepted.queryId, requestedAt: new Date().toISOString() }
      );
      // 화면의 onSuccess 는 화면이 사라지면 안 돈다 — 알림 감시는 여기서 건다.
      useCandidateQueryWatchStore
        .getState()
        .watch({ scenarioId, country: input.region, queryId: accepted.queryId });
    },
  });
}

/**
 * 재시도는 **`failed` 만**이고 최대 3회다. 화면이 그 판정을 하지 않는다 — 서버가 409 로 막고
 * 여기서는 그 오류를 그대로 올린다(게이트 §3 불변 10 과 같은 규율).
 */
export function useRetryCandidateQuery(scenarioId: string, country: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (queryId: string) => retryCandidateQuery(scenarioId, queryId),
    onSuccess: (_result, queryId) => {
      // 낡은 `failed` 를 그대로 두면 새로 서는 감시자가 그것을 읽고 곧바로 거둔다 — 다시 대기로 돌린다.
      queryClient.setQueryData<CandidateQueryDetail>(
        candidateKeys.query(scenarioId, queryId),
        (old) => (old ? { ...old, state: 'queued', error: null, progress: null } : old)
      );
      void queryClient.invalidateQueries({
        queryKey: candidateKeys.query(scenarioId, queryId),
      });
      // 실패 알림으로 거둔 감시를 다시 건다 — 응답 전에 화면을 떠나도 끝남이 알려진다.
      useCandidateQueryWatchStore.getState().watch({ scenarioId, country, queryId });
    },
  });
}

/**
 * 페이지는 **준비된 뒤에만** 부른다. 준비 전에 부르면 서버가 409 이고, 그 409 를 빈 목록으로
 * 접으면 화면이 "조건에 맞는 사람이 없다" 로 읽는다.
 */
export function useCandidatePage(
  scenarioId: string,
  queryId: string | null,
  cursor: string | null,
  options: { enabled: boolean }
) {
  return useQuery({
    queryKey: candidateKeys.page(scenarioId, queryId ?? '', cursor),
    queryFn: () =>
      fetchCandidatePage(scenarioId, queryId as string, {
        cursor: cursor ?? undefined,
      }),
    enabled: options.enabled && queryId !== null,
    // 스냅샷은 얼려 있다 — 같은 커서면 같은 답이라 다시 부를 이유가 없다.
    staleTime: Infinity,
  });
}

export function useOutreachSelection(scenarioId: string, country: string | null) {
  return useQuery({
    queryKey: candidateKeys.selection(scenarioId, country ?? ''),
    queryFn: () => fetchOutreachSelection(scenarioId, country as string),
    enabled: country !== null,
    staleTime: 30_000,
  });
}

export function useSaveOutreachSelection(scenarioId: string, country: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SaveOutreachSelectionRequest) =>
      saveOutreachSelection(scenarioId, country, body),
    onSuccess: (row) => {
      queryClient.setQueryData(candidateKeys.selection(scenarioId, country), row);
      // 직접 뺀 사람이 다른 매체 목록에 남지 않게 한다 — 그 목록은 얼린 스냅샷이라 스스로 다시 안 받는다.
      void queryClient.invalidateQueries({
        queryKey: ['outreach-candidate', 'other-channel', scenarioId],
      });
    },
  });
}

/**
 * 그 페이지 후보들의 최근 접촉·기본 제외 — IA §3-3 「행 한 줄」.
 *
 * 표가 그려진 **뒤에** 채워진다. 크롤러 페이지와 한 호출로 묶지 않는 이유는 원천이 다르기
 * 때문이다 — 이쪽이 늦거나 죽어도 후보 목록 자체는 서야 한다.
 *
 * id 가 비면 호출하지 않는다. 빈 배열로 물으면 서버가 빈 답을 주지만 왕복이 공짜는 아니다.
 */
export function useCandidateStatuses(scenarioId: string, externalCreatorIds: string[]) {
  return useQuery({
    queryKey: candidateKeys.statuses(scenarioId, externalCreatorIds),
    queryFn: () => fetchCandidateStatuses(scenarioId, externalCreatorIds),
    enabled: externalCreatorIds.length > 0,
    // 관계 이력은 이 화면 밖에서도 움직인다(회신 폴링·폼 인입). 오래 안 들고 있는다.
    staleTime: 30_000,
  });
}

/** 나라별 진행 줄 — 발송을 누르면 바뀌므로 발송 뒤에 이 키를 무효화한다. */
export function useOutreachProgress(scenarioId: string) {
  return useQuery({
    queryKey: candidateKeys.progress(scenarioId),
    queryFn: () => fetchOutreachProgress(scenarioId),
  });
}

/**
 * 발송 확인 면의 명단 미리보기.
 *
 * **오래된 값을 안 들고 있는다.** 작성하는 동안 회신·폼 제출이 생기면 최종 명단이 줄어드는데
 * (IA §3-5) 그걸 늦게 알면 이미 빠진 사람 수로 발송을 누른다.
 */
export function useOutreachBulkPreview(
  scenarioId: string,
  draftId: string | null,
  perCreatorSendIds: string[],
  /** 이번 발송에 다시 넣을 기본 제외자(12bcfe19). 초안에는 없고 요청에만 실린다. */
  reincludeIds: string[] = []
) {
  return useQuery({
    queryKey: candidateKeys.bulkPreview(scenarioId, draftId ?? '', perCreatorSendIds, reincludeIds),
    queryFn: () =>
      previewOutreachBulk(scenarioId, {
        draftId: draftId as string,
        perCreatorSendIds,
        reincludeIds,
      }),
    enabled: draftId !== null,
    staleTime: 0,
    // 따로 보내기를 켜고 끌 때마다 면 전체가 「확인하는 중」 으로 깜빡이지 않게 앞 답을 들고 있는다.
    placeholderData: keepPreviousData,
  });
}

/** 이상치 묶음 건수 — 발송 확인 면의 「다시 볼 것」 이 읽는다. 건수만 쓰므로 미리보기 행은 안 받는다. */
export function useOutreachOutlierSummary(scenarioId: string, draftId: string | null) {
  return useQuery({
    queryKey: candidateKeys.outlierSummary(scenarioId, draftId ?? ''),
    queryFn: () => fetchOutlierSummary(scenarioId, draftId as string, 0),
    enabled: draftId !== null,
    staleTime: 0,
  });
}

/**
 * 대량 발송. 성공하면 이 캠페인의 후보 쪽 읽기(미리보기·진행 줄·이상치)를 전부 버린다 — 방금
 * 나간 사람이 「이미 받음」 으로 바뀌고 회차 번호가 하나 늘기 때문이다. 발송 이력은 다른 슬라이스의
 * 키라 부르는 쪽이 `onSuccess` 에서 다시 부른다.
 */
export function useSendOutreachBulk(scenarioId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: OutreachBulkSendRequest) => sendOutreachBulk(scenarioId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: candidateKeys.all });
    },
  });
}

/** 예약 발송 (96445435) — 성공하면 진행 줄·명단을 다시 부른다. 이력은 화면이 다시 부른다. */
export function useReserveOutreachBulk(scenarioId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: OutreachBulkReserveRequest) => reserveOutreachBulk(scenarioId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: candidateKeys.all });
    },
  });
}

/** 예약 취소 (96445435). 취소가 확정되면 그 회차 사람은 다시 보낼 수 있으므로 명단을 다시 부른다. */
export function useCancelOutreachReservation(scenarioId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (roundId: string) => cancelOutreachReservation(scenarioId, roundId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: candidateKeys.all });
    },
  });
}

/**
 * 「먼저 볼 것」 묶음 — 승인 시안 CandidatesAfter.
 *
 * **저장할 때마다 다시 묻지 않는다.** 한 번 부를 때마다 서버가 명단 전체(2만 명이면 스냅샷 id
 * 네 번 + 주소 조회 스무 번)를 다시 가른다. 그래서 명단의 모양이 바뀔 때만(`membership` — 스냅샷·
 * 모드가 바뀌거나 직접 고른 명단이 바뀔 때) 새로 받고, 묶음에서 뺀 것은 화면이 겹쳐 셈한다.
 */
export function useOutlierSummary(scenarioId: string, draftId: string | null, membership: string) {
  return useQuery({
    queryKey: candidateKeys.outliers(scenarioId, draftId ?? '', membership),
    queryFn: () => fetchOutlierSummary(scenarioId, draftId as string),
    enabled: draftId !== null,
    staleTime: Infinity,
  });
}

/** 묶음 전체 행의 한 페이지 — 「N명 전부 보기」. */
export function useOutlierRows(
  scenarioId: string,
  draftId: string,
  code: OutreachOutlierGroupCode,
  offset: number
) {
  return useQuery({
    queryKey: candidateKeys.outlierRows(scenarioId, draftId, code, offset),
    queryFn: () => fetchOutlierRows(scenarioId, { draftId, code, offset }),
    staleTime: 60_000,
  });
}

/** 이메일이 없는 후보 — 열었을 때만 부르고, 더 보기는 이어 볼 표시로 한 덩어리씩 받는다. */
export function useOtherChannelCandidates(
  scenarioId: string,
  queryId: string | null,
  options: { enabled: boolean }
) {
  return useInfiniteQuery({
    queryKey: candidateKeys.otherChannel(scenarioId, queryId ?? ''),
    queryFn: ({ pageParam }) =>
      fetchOtherChannelCandidates(scenarioId, queryId as string, {
        ...(pageParam ? { cursor: pageParam } : {}),
        limit: 50,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: options.enabled && queryId !== null,
    // 스냅샷은 얼려 있다.
    staleTime: Infinity,
  });
}
